import { SignalElementConfiguration } from "@domain/project/configurations/signal-element-configuration";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { Exclude, Expose } from "class-transformer";
import { IsString } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class FloorplanSignalElement extends FloorplanProductItem<SignalElementConfiguration> {
  @Exclude()
  private readonly _signalElement: SignalElementConfiguration;

  @IsString()
  @Expose({ name: "signalElementId" })
  private readonly _signalElementId: string;

  constructor(
    floorplan: Floorplan,
    signalElement: SignalElementConfiguration,
    id: string,
    x: number,
    y: number,
    size: number,
    notes?: string,
  ) {
    super(floorplan, signalElement, id, x, y, size, notes);
    this._signalElementId = signalElement?.id;
    this._signalElement = signalElement;
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.signalElementsItemTextVisible;
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanSignalElement) | undefined {
    const config = this.config;
    const size = this.size;

    return (x: number, y: number) => FloorplanSignalElement.create(this._floorplan, config, x, y, size);
  }

  override delete() {
    this._floorplan.deleteSignalElement(this);
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.signalElements;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as FloorplanSignalElement,
        this._floorplan.signalElements,
        FloorplanEventType.SIGNAL_ELEMENT_ADDED,
        () => this._floorplan.project.setPositionNumbers(),
        afterDuplicateAdded as ((duplicate: FloorplanSignalElement) => void) | undefined,
      );
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshSignalElementsLock();
  }

  static create(
    floorplan: Floorplan,
    signalElement: SignalElementConfiguration,
    x: number,
    y: number,
    size: number,
  ): FloorplanSignalElement {
    return new FloorplanSignalElement(floorplan, signalElement, uuidv4(), x, y, size);
  }

  protected override getConfigFromProject(floorplan: Floorplan): SignalElementConfiguration | undefined {
    return floorplan.getSignalElementConfig(this._signalElementId);
  }
}
